/**
 * Late work orders (design S2.5 OverdueLogin, DlgExtend; P1-2.5, WO3):
 * - `MyOverdueDialog` — at sign-in, the caller's own late orders (they are responsible): due date and days
 *   late, a status over its limit, the reason they gave — editable in place. Shown once per session whenever
 *   there is one; it cannot be switched off (31.07). "Open list" goes to the overdue view.
 * - `ExtendDialog` — the manager's later due date with a reason (`service.wo.extend`).
 * - `OverdueReasonDialog` — the responsible's reason on the card.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, DatePicker, Flex, Input, Modal, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useStatusName } from './StatusTag';
import * as api from '../api/overdueApi';
import { workOrderKeys } from '../api/queryKeys';
import { useCardMutation } from '../hooks/useWorkOrders';
import { useWorkOrderError } from '../utils/useWorkOrderError';

import type { OverdueRow } from '../api/overdueApi';
import type { WorkOrderCard } from '../types/workOrderTypes';

import { useAuth } from '@/shared/auth/AuthProvider';

const READERS = ['service.read', 'service.read.all', 'service.wo.write'];
const MY_OVERDUE = [...workOrderKeys.all, 'my-overdue'] as const;

/**
 * "Shown in this sign-in" lives in the query cache, keyed by the user: the cache is cleared on logout
 * (AuthProvider), so a new sign-in — or another user in the same tab — sees the list again. Nothing
 * observes the flag, so it must never be garbage-collected (the app's gcTime is 10 minutes).
 */
const SHOWN = [...workOrderKeys.all, 'my-overdue-shown'] as const;
const shownKey = (userId: string): readonly unknown[] => [...SHOWN, userId];

export function MyOverdueDialog(): React.JSX.Element | null {
  const { t } = useTranslation(['workOrders', 'service']);
  const navigate = useNavigate();
  const { user, hasCapability } = useAuth();
  const queryClient = useQueryClient();
  const statusName = useStatusName();
  const canRead = READERS.some(hasCapability);
  const [open, setOpen] = useState(false);
  const mine = useQuery({ queryKey: MY_OVERDUE, queryFn: api.fetchMyOverdue, enabled: canRead && user !== null, staleTime: Infinity, meta: { suppressGlobalError: true } });
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (user === null || (mine.data ?? []).length === 0) return;
    if (queryClient.getQueryData(shownKey(user.id)) === true) return;
    queryClient.setQueryDefaults(SHOWN, { gcTime: Infinity });
    queryClient.setQueryData(shownKey(user.id), true);
    setOpen(true);
  }, [mine.data, user, queryClient]);

  if (!open) return null;
  const rows = mine.data ?? [];
  const save = async (row: OverdueRow): Promise<void> => {
    const reason = draft.trim() === '' ? null : draft.trim();
    await api.setOverdueReason(row.id, reason);
    // The saved text shows at once, and the list is read again from the server.
    queryClient.setQueryData<OverdueRow[]>(MY_OVERDUE, (rows) => rows?.map((r) => (r.id === row.id ? { ...r, reason } : r)));
    setEditing(null);
    await queryClient.invalidateQueries({ queryKey: MY_OVERDUE });
  };

  return (
    <Modal
      open
      width={760}
      title={t('overdue.title', { count: rows.length })}
      onCancel={() => setOpen(false)}
      footer={
        <Space>
          <Button
            onClick={() => {
              setOpen(false);
              navigate('/work-orders?view=overdue');
            }}
          >
            {t('overdue.openList')}
          </Button>
          <Button type="primary" onClick={() => setOpen(false)}>
            {t('overdue.ok')}
          </Button>
        </Space>
      }
    >
      <Table<OverdueRow>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={rows}
        columns={[
          {
            title: t('overdue.col.order'),
            key: 'number',
            render: (_v, r) => (
              <Link to={`/work-orders/${r.id}`} onClick={() => setOpen(false)}>
                {r.number}
              </Link>
            ),
          },
          { title: t('overdue.col.vehicle'), key: 'vehicle', render: (_v, r) => r.vehicle ?? '—' },
          {
            title: t('overdue.col.overdue'),
            key: 'overdue',
            render: (_v, r) => (
              <div>
                {r.dueLate ? <div>{t('overdue.due', { date: dayjs(r.dueOn).format('DD.MM'), count: r.daysLate })}</div> : null}
                {r.statusLimitHours !== null && r.statusHours !== null ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('overdue.inStatus', { status: statusName(r.statusId), hours: r.statusHours, limit: r.statusLimitHours })}
                  </Typography.Text>
                ) : null}
              </div>
            ),
          },
          {
            title: t('overdue.col.reason'),
            key: 'reason',
            render: (_v, r) =>
              editing === r.id ? (
                <Space.Compact style={{ width: '100%' }}>
                  <Input aria-label={t('overdue.reason')} maxLength={500} value={draft} onChange={(e) => setDraft(e.target.value)} />
                  <Button type="primary" onClick={() => void save(r)}>
                    {t('common.save')}
                  </Button>
                </Space.Compact>
              ) : (
                <Space>
                  {r.reason !== null ? <Typography.Text type="secondary">{r.reason}</Typography.Text> : null}
                  <Button
                    size="small"
                    onClick={() => {
                      setEditing(r.id);
                      setDraft(r.reason ?? '');
                    }}
                  >
                    {r.reason !== null ? t('overdue.edit') : t('overdue.addReason')}
                  </Button>
                </Space>
              ),
          },
        ]}
      />
    </Modal>
  );
}

export function ExtendDialog({ order, onClose }: { order: WorkOrderCard; onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation('workOrders');
  const describe = useWorkOrderError();
  const [dueOn, setDueOn] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useCardMutation(order.id, (v: { dueOn: string; reason: string }) => api.extendDue(order.id, { version: order.version, ...v }));
  const late = dayjs().startOf('day').diff(dayjs(order.dueOn), 'day');
  const valid = dueOn !== null && dueOn > order.dueOn && reason.trim() !== '';
  return (
    <Modal
      open
      title={t('extend.title', { number: order.number })}
      okText={t('extend.confirm')}
      cancelText={t('common.cancel')}
      okButtonProps={{ disabled: !valid, loading: save.isPending }}
      onCancel={onClose}
      onOk={() => dueOn !== null && save.mutate({ dueOn, reason: reason.trim() }, { onSuccess: onClose, onError: (e) => setError(describe(e)) })}
    >
      <Flex vertical gap={12}>
        {error !== null ? <Alert type="error" showIcon message={error} /> : null}
        <Typography.Text type="secondary">
          {t('fields.due')} {dayjs(order.dueOn).format('DD.MM.YYYY')}
          {late > 0 ? ` · ${t('list.daysLate', { count: late })}` : ''}
        </Typography.Text>
        <div>
          <Typography.Text>
            {t('extend.newDue')} <Typography.Text type="danger">*</Typography.Text>
          </Typography.Text>
          <DatePicker
            aria-label={t('extend.newDue')}
            format="DD.MM.YYYY"
            style={{ width: '100%' }}
            value={dueOn === null ? null : dayjs(dueOn)}
            disabledDate={(d) => !d.isAfter(dayjs(order.dueOn), 'day') || d.isBefore(dayjs(), 'day')}
            onChange={(d) => setDueOn(d === null ? null : d.format('YYYY-MM-DD'))}
          />
        </div>
        <div>
          <Typography.Text>
            {t('extend.reason')} <Typography.Text type="danger">*</Typography.Text>
          </Typography.Text>
          <Input.TextArea aria-label={t('extend.reason')} rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </Flex>
    </Modal>
  );
}

export function OverdueReasonDialog({ order, onClose }: { order: WorkOrderCard; onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation('workOrders');
  const { message } = App.useApp();
  const describe = useWorkOrderError();
  const [reason, setReason] = useState(order.overdueReason ?? '');
  const save = useCardMutation(order.id, (v: string | null) => api.setOverdueReason(order.id, v));
  return (
    <Modal
      open
      title={t('overdue.reasonTitle', { number: order.number })}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      okButtonProps={{ loading: save.isPending }}
      onCancel={onClose}
      onOk={() => save.mutate(reason.trim() === '' ? null : reason.trim(), { onSuccess: onClose, onError: (e) => void message.error(describe(e)) })}
    >
      <Input.TextArea aria-label={t('overdue.reason')} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
    </Modal>
  );
}
